import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import {
  RequestPaymentTermsAcceptance,
  RequestTermsAcceptance,
} from 'src/lib/services/api/request-api';
import { joinExistingValues } from 'src/helpers/joinExistingValues';
import { useFormatter } from 'src/services/i18n';
import { UserResponse } from 'src/lib/services/api/user-api';

const displayName = (user?: UserResponse | null) =>
  user ? joinExistingValues([user.first_name, user.last_name], ' ') || user.email : '';

const TermsAcceptanceLine: React.FC<{
  title: string;
  acceptedAt: string;
  detail?: string | null;
}> = ({ title, acceptedAt, detail }) => {
  const { formatDateTime } = useFormatter();

  return (
    <div
      className={
        'tw-flex tw-items-start tw-gap-3 tw-rounded-md tw-border tw-border-green-200 tw-bg-green-50 tw-p-3'
      }
    >
      <CheckCircle2 size={20} className={'tw-mt-0.5 tw-shrink-0 tw-text-green-600'} />
      <div className={'tw-flex tw-flex-col tw-gap-0.5'}>
        <span className={'tw-font-medium'}>{title}</span>
        <span className={'tw-text-sm tw-text-text-subtle'}>
          {formatDateTime(acceptedAt)}
          {detail ? ` · ${detail}` : ''}
        </span>
      </div>
    </div>
  );
};

/**
 * Pinned at the top of a request's activity log (#2784): when the client
 * accepted our Terms & Conditions, next to the download entries, so all the
 * dispute evidence is on one screen.
 *
 * - At payment: the client ticked "I agree to the Terms of Service" on the
 *   Stripe payment page. Strongest proof — Stripe holds it with the payment.
 * - At sign-up: the sign-up form states that creating an account is agreeing
 *   to the Terms; the date is when the company owner's account was created.
 */
const RequestActivityTermsAcceptance: React.FC<{
  signUp?: RequestTermsAcceptance | null;
  payment?: RequestPaymentTermsAcceptance | null;
}> = ({ signUp, payment }) => {
  const { t } = useTranslation();

  if (!signUp && !payment) return null;

  return (
    <div className={'tw-flex tw-flex-col tw-gap-2'}>
      {payment && (
        <TermsAcceptanceLine
          title={
            payment.user
              ? t('pages/admin:request.request_activity.terms_accepted_payment', {
                  defaultValue: '{{name}} accepted the Terms & Conditions at payment',
                  name: displayName(payment.user),
                  interpolation: { escapeValue: false },
                })
              : t('pages/admin:request.request_activity.terms_accepted_payment_anonymous', {
                  defaultValue: 'The Terms & Conditions were accepted at payment',
                })
          }
          acceptedAt={payment.accepted_at}
          detail={
            payment.stripe_checkout_session_id
              ? t('pages/admin:request.request_activity.stripe_session', {
                  defaultValue: 'Stripe checkout {{id}}',
                  id: payment.stripe_checkout_session_id,
                })
              : null
          }
        />
      )}
      {signUp && (
        <TermsAcceptanceLine
          // Worded as what happened: the sign-up form has no checkbox, its
          // small print says registering means agreeing. The ticked box is
          // the payment line above.
          title={t('pages/admin:request.request_activity.terms_accepted_by_sign_up', {
            defaultValue: '{{name}} agreed to the Terms & Conditions by signing up',
            name: displayName(signUp.user),
            interpolation: { escapeValue: false },
          })}
          acceptedAt={signUp.accepted_at}
        />
      )}
    </div>
  );
};

export { RequestActivityTermsAcceptance };
